import React,{Component} from 'react'
import {GradientDefs, GradientFillUrl} from "shape-edit/forms/fields";
import {DEFAULT_LINE_CAP, DEFAULT_LINE_JOIN, LINE_CAP_CONSTS, LINE_JOIN_CONSTS} from "shape-edit/forms/helpers";
import { Size } from 'app/types/model-types';
import RatingShape from 'shape-entities/types/Rating';
import { DEFAULT_FONT_WEIGHT } from 'shape-entities/render-text';
import { CSSProperties } from '@mui/material';
import trans from 'trans';
import { iconDisabledColor } from 'template-edit/components/tools-panel';
import { debounce } from 'lodash';
import StrokeUnderlayerFilter, { getStrokeUnderlayerFilterId, strokeUnderlayerFilterUrl } from 'svg-text-utils/StrokeUnderlayerFilter';
import { checkOverflowAndResizeShape, getRatingShapeFontSize, getRatingShapeSize } from 'template-edit/components/helper';

interface Props {
    shape: RatingShape
    updateShape: Function
}

interface State {
    newSize: Size
}

const { min, max, round, floor, ceil } = Math

export const STAR_SYMBOLS = [
    "M20,2 L22.9,16 L37.1,14.4 L24.8,21.5 L30.6,34.6 L20,25 L9.4,34.6 L15.2,21.5 L2.9,14.4 L17.1,16 Z",
    "M20,3 L24.1,14.3 L36.2,14.7 L26.7,22.2 L30,33.8 L20,27 L10,33.8 L13.3,22.2 L3.8,14.7 L15.9,14.3 Z",
    "M20,2 L25.3,12.7 L37.1,14.4 L28.6,22.8 L30.6,34.6 L20,29 L9.4,34.6 L11.4,22.8 L2.9,14.4 L14.7,12.7 Z",
    "M20,3 L26.5,11.1 L36.2,14.7 L30.5,23.4 L30,33.8 L20,31 L10,33.8 L9.5,23.4 L3.8,14.7 L13.5,11.1 Z",
    "M20,2 Q22.6,5.2 24.1,14.3 Q33.3,12.9 37.1,14.4 Q34.9,17.9 26.7,22.2 Q30.8,30.5 30.6,34.6 Q26.6,33.5 20,27 Q13.4,33.5 9.4,34.6 Q9.2,30.5 13.3,22.2 Q5.1,17.9 2.9,14.4 Q6.8,12.9 15.9,14.3 Q17.4,5.2 20,2 Z",
    "M20,2 Q21.4,11.7 24.1,14.3 Q32.7,13.2 37.1,14.4 Q28.3,18.8 26.7,22.2 Q30.4,30.1 30.6,34.6 Q23.7,27.6 20,27 Q13.6,33 9.4,34.6 Q13.9,25.9 13.3,22.2 Q5.7,18 2.9,14.4 Q12.6,16 15.9,14.3 Q17.5,5.8 20,2 Z",
]

const STAR_NATURAL_SIZE = 40
const HIDDEN_OPACITY = .2

class RatingSvg extends Component<Props, State> {
    underlayerFilterId = getStrokeUnderlayerFilterId()

    constructor(props) {
        super(props)

        this.state = {
            newSize: null,
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps && (
            (prevProps.shape.text_fontsize !== this.props.shape.text_fontsize) ||
            (prevProps.shape.text_fontfamily !== this.props.shape.text_fontfamily) ||
            (prevProps.shape.letter_spacing !== this.props.shape.letter_spacing) ||
            (prevProps.shape.text_padding !== this.props.shape.text_padding) ||
            (prevProps.shape.x !== this.props.shape.x) ||
            (prevProps.shape.y !== this.props.shape.y) ||
            (prevProps.shape.width !== this.props.shape.width) ||
            (prevProps.shape.height !== this.props.shape.height)
        )) {
            this.checkOverflowDebounced()
        }
    }

    checkOverflowDebounced = debounce(() => {
        this.checkOverflow()
    }, 500)

    checkOverflow = () => {
        const { shape } = this.props
        const rs = getRatingShapeSize(shape.text_fontsize, shape.text_padding, shape.stroke_width)
        checkOverflowAndResizeShape(shape, rs, this.props.updateShape)
    }

    setRealSize = (newSize: Size) => {
        this.setState({newSize: newSize})
    }

    renderStars = (rating: number, filter: string) => {
        const {text_fontsize, color, gradient_id, gradient, stroke_width, line_join, line_cap, index, rating_min_stars, star_symbol} = this.props.shape
        const isHidden = rating_min_stars && rating < rating_min_stars

        const allIndexes = [0, 1, 2, 3, 4]
        const emptyColor = 'lightgray'
        const starBoxSize = text_fontsize;
        const scaleSymbol = text_fontsize / STAR_NATURAL_SIZE;
        const gap = .5 * starBoxSize;

        const fillColor = isHidden ? iconDisabledColor : (gradient_id && gradient ? GradientFillUrl(gradient_id) : color)
        const currentPath = star_symbol !== null && typeof STAR_SYMBOLS[star_symbol] !== 'undefined' ? STAR_SYMBOLS[star_symbol] : STAR_SYMBOLS[0]

        return (
            <g>
                <defs>
                    {allIndexes.map(n => {
                        const fillAmount = min(1, max(0, rating - n));
                        if (fillAmount > 0 && fillAmount < 1) {
                            return (
                                <clipPath key={n} id={`star-clip-${index}-${n}`}>
                                    <rect x={0} y={0} width={starBoxSize * fillAmount / scaleSymbol} height={starBoxSize/scaleSymbol} />
                                </clipPath>
                            );
                        }
                        return null;
                    })}
                </defs>

                {allIndexes.map(n => {
                    const fillAmount = min(1, max(0, rating - n));
                    const clipId = `star-clip-${index}-${n}`;

                    return (
                        <g key={n} filter={filter} transform={`translate(${n * starBoxSize - 5 * starBoxSize - .5*gap}, ${-.5 * starBoxSize}) scale(${scaleSymbol})`} style={{opacity: isHidden ? HIDDEN_OPACITY : 1}}>
                            <path
                                fill={'transparent'}
                                stroke={emptyColor}
                                strokeWidth={stroke_width}
                                d={currentPath}
                                strokeLinejoin={line_join && LINE_JOIN_CONSTS[line_join] ? LINE_JOIN_CONSTS[line_join] : DEFAULT_LINE_JOIN}
                                strokeLinecap={line_cap && LINE_CAP_CONSTS[line_cap] ? LINE_CAP_CONSTS[line_cap] : DEFAULT_LINE_CAP}
                            />

                            {fillAmount > 0 && (
                                <path
                                    stroke={fillColor}
                                    fill={fillColor}
                                    strokeWidth={stroke_width}
                                    d={currentPath}
                                    strokeLinejoin={line_join && LINE_JOIN_CONSTS[line_join] ? LINE_JOIN_CONSTS[line_join] : DEFAULT_LINE_JOIN}
                                    strokeLinecap={line_cap && LINE_CAP_CONSTS[line_cap] ? LINE_CAP_CONSTS[line_cap] : DEFAULT_LINE_CAP}
                                    clipPath={fillAmount < 1 ? `url(#${clipId})` : undefined}
                                />
                            )}
                        </g>
                    );
                })}
            </g>
        );
    }


    render() {
        const { shadow_color, shadow_opacity, shadow_sigma, gradient, gradient_id, color, text_color, text_background_color, index,
            letter_spacing, text_fontsize, text_fontfamily, text_padding, width, height, corner_radius, stroke_width,
            text_stroke_width, text_stroke_underlayer, text_stroke_color, text_content_replaced, shadow_xoffset, shadow_yoffset,
            rating_max_value, rating_hide_empty, rating_min_stars, rating_from_depots
        } = this.props.shape

        const starBoxSize = text_fontsize
        const gap = .5 * starBoxSize;
        const isShadow = shadow_color && shadow_sigma
        const fillColor = gradient_id && gradient ? GradientFillUrl(gradient_id) : color
        const fillOpacity = gradient_id && gradient && gradient.opacity ? gradient.opacity / 100 : 1
        const letterSpacing = letter_spacing ? letter_spacing : 0

        const textStyle: CSSProperties = {
            fontWeight: DEFAULT_FONT_WEIGHT,
            fontSize: text_fontsize + "px",
            fontFamily: `"${text_fontfamily}","Unicode-BMP-Fallback-SIL"`,
            width: width + 'px',
            letterSpacing: letterSpacing ? `${letterSpacing}px` : undefined,
            strokeWidth: text_stroke_width || 0,
            stroke: text_stroke_color || undefined,
        }

        const isStrokeUnderlayer = !!text_stroke_underlayer && !!text_stroke_color && !!text_stroke_width
        const topTextStyle: CSSProperties = isStrokeUnderlayer ? { ...textStyle, stroke: undefined, strokeWidth: 0 } : textStyle

        //with stroke underlayer, the stroked text is rendered first and the text without stroke on top of it
        const renderText = (style: CSSProperties, content: React.ReactNode) => <React.Fragment>
            {isStrokeUnderlayer &&
                <g filter={filter}>
                    <StrokeUnderlayerFilter id={this.underlayerFilterId} strokeWidth={text_stroke_width} box={{ x: 0, y: 0, width, height }} />
                    <text
                        dominantBaseline={'central'}
                        textAnchor={'start'}
                        fill={text_stroke_color}
                        style={{ ...textStyle, ...style, pointerEvents: 'none' }}
                        filter={strokeUnderlayerFilterUrl(this.underlayerFilterId, text_stroke_width)}>
                        {content}
                    </text>
                </g>
            }
            <text
                dominantBaseline={'central'}
                textAnchor={'start'}
                fill={textColor}
                style={{ ...topTextStyle, ...style }}
                filter={isStrokeUnderlayer ? undefined : filter}>
                {content}
            </text>
        </React.Fragment>

        const filter = isShadow ? `url(#shadow-${index})` : ''

        const ratingAndRatingCount = text_content_replaced ? text_content_replaced.split('_') : null
        const rating = ratingAndRatingCount && ratingAndRatingCount[0] ? ratingAndRatingCount[0] : null
        const ratingCount = ratingAndRatingCount && ratingAndRatingCount[1] ? ratingAndRatingCount[1] : null
        const ratingFromZeroToFive = rating ? (
            rating_from_depots ? parseFloat(`${rating}`) : (rating_max_value ? round(10 * 5 * parseFloat(`${rating}`) / rating_max_value) / 10 : null)
         ) : null

        const isHidden = rating_min_stars && ratingFromZeroToFive < rating_min_stars
        const textColor = isHidden ? iconDisabledColor : text_color

        return (
            <g className="draggable">
                {text_background_color &&
                    <rect
                        x={-.5 * width}
                        y={-.5 * height}
                        width={width}
                        height={height}
                        fill={text_background_color}
                        rx={corner_radius}
                        ry={corner_radius}
                    />
                }

                {isShadow &&
                    <filter id={`shadow-${index}`} width="200%" height="200%" y="-50%" x="-50%">
                        <feDropShadow
                            dx={shadow_xoffset}
                            dy={shadow_yoffset}
                            stdDeviation={shadow_sigma}
                            floodColor={shadow_color}
                            floodOpacity={1 - shadow_opacity / 100}
                        />
                    </filter>
                }

                {gradient_id && gradient &&
                    <GradientDefs gradient={gradient} idx={gradient_id} />
                }

                <g id={`stars-${index}`}>
                    {(rating_hide_empty && !ratingAndRatingCount) ?
                        renderText({
                            fontSize: `${text_fontsize}px`,
                            pointerEvents: 'auto',
                            transform: `translate(${-.5 * width}px, 0)`
                        }, trans('empty_element')) :
                        <React.Fragment>
                            {this.renderStars(ratingFromZeroToFive, filter)}

                            {ratingFromZeroToFive &&
                                renderText({
                                    fontSize: `${text_fontsize}px`,
                                    pointerEvents: 'auto',
                                    transform: `translate(${.5 * gap}px, 0)`,
                                    opacity: isHidden ? HIDDEN_OPACITY : 1,
                                }, <React.Fragment>{ratingFromZeroToFive} {ratingCount && `(${ratingCount})`}</React.Fragment>)
                            }

                            {isHidden &&
                                <line
                                    stroke={textColor}
                                    strokeWidth={stroke_width + 1}
                                    x1={-.5 * width}
                                    x2={.3 * width}
                                    y1={0}
                                    y2={0}
                                    style={{
                                        opacity: HIDDEN_OPACITY,
                                    }}
                                />
                            }
                        </React.Fragment>
                    }
                </g>

                {/* debug only */}
                {/* <rect width={width} height={height} x={-.5 * width} y={-.5 * height} stroke='green' strokeWidth={1} fill='none' />
                <rect width={.5 * width} height={.5 * height} x={-.5 * width} y={-.5 * height} stroke='green' strokeWidth={1} fill='none' />
                <rect width={.5 * width} height={.5 * height} x={0} y={0} stroke='green' strokeWidth={1} fill='none' /> */}
                {/* debug only */}
            </g>
        )
    }
}

export default RatingSvg
